import { getPlural, pluralizeToString } from 'lib/string/pluralize'
import { capitalise } from 'lib/string/stringUtils'
import { fareTypeLabelMap, FlightsFareTypes } from 'constants/flight'
import moment from 'moment'
import { SHORT_DAY_NAME_DAY_MONTH_YEAR } from 'constants/dateFormats'
import { last, sum } from 'lib/array/arrayUtils'

interface Supplement {
  validatingCarrierName: string
  validatingCarrierLogo: string
}

export function buildFlightItemBreakdownView(
  itemView: App.Checkout.FlightItemView,
  segment: App.Checkout.FlightItemViewJourney,
  supplement: Supplement,
): App.Checkout.FlightItemBreakdownView {
  return {
    itemId: itemView.item.itemId,
    title: formatJourneyShorthand(segment.journeyFlight),
    additionalInfo: [{ text: formatJourneyDepartureDate(segment.journeyFlight) }],
    itemType: 'flight' as const,
    additionalElements: [],
    price: segment.cost || segment.journeyFlight.cost,
    journey: segment.journeyFlight,
    validatingCarrierLogo: supplement.validatingCarrierLogo,
    validatingCarrierName: supplement.validatingCarrierName,
    isBundled: itemView.isBundled,
    taxesAndFees: itemView.totals.taxesAndFees,
    fareFamily: segment.fareFamily?.fareFamily.description,
    itemView,
  }
}

export function buildDateChangeFlightItemBreakdownView(
  itemView: App.Checkout.FlightItemView,
): App.Checkout.FlightsDateChangeFeeBreakdownView {
  const passengers = itemView.passengers
  const flights = itemView.flights

  const travellerCounts = {
    adult: passengers.filter(p => p.type === 'adult').length,
    child: passengers.filter(p => p.type === 'child').length,
    infant: passengers.filter(p => p.type === 'infant').length,
  }

  const priceMap = {
    adult: sum(flights.map(f => f.journeyFlight.priceBreakdown?.adults.cost ?? 0)) * travellerCounts.adult,
    child: sum(flights.map(f => f.journeyFlight.priceBreakdown?.children?.cost ?? 0)) * travellerCounts.child,
    infant: sum(flights.map(f => f.journeyFlight.priceBreakdown?.infants?.cost ?? 0)) * travellerCounts.infant,
  }

  const additionalElements = Object.entries(travellerCounts)
    .filter(([_, count]) => count > 0)
    .map(([type, count]) => ({
      id: type,
      left: {
        type: 'text' as const,
        value: pluralizeToString(type, count),
      },
      right: {
        type: 'price' as const,
        value: priceMap[type as keyof typeof priceMap],
      },
    }))

  return {
    itemType: 'flights-date-change-fee' as const,
    title: '',
    additionalInfo: [],
    additionalElements,
  }
}

export function formatJourneyDepartureDate(journey: App.JourneyFlight): string {
  return moment(journey.flights[0].departingDate).format(SHORT_DAY_NAME_DAY_MONTH_YEAR)
}

export function formatFareType(type: 'return' | 'oneWay' | 'multiCity'): string {
  const titleNoun = type === FlightsFareTypes.ONE_WAY ? 'flight' : 'flights'
  return `${fareTypeLabelMap[type]} ${titleNoun}`
}

export function formatJourneyShorthand(journey: App.JourneyFlight): string {
  const journeyDepartureLocation = journey.flights[0].departingDisplayNames.primary
  const journeyArrivalLocation = last(journey.flights).arrivalDisplayNames.primary
  return `${journeyDepartureLocation} to ${journeyArrivalLocation}`
}

export function formatJourneySummaryTitle(journeys: Array<App.Checkout.FlightItemViewJourney>, fareType: FlightsFareTypes): string {
  if ([FlightsFareTypes.RETURN, FlightsFareTypes.ONE_WAY].includes(fareType)) {
    return formatJourneyShorthand(journeys[0].journeyFlight)
  }

  const departureLocation = journeys[0].journeyFlight.flights[0].departingDisplayNames.primary
  const lastJourney = last(journeys)
  const arrivalLocation = last(lastJourney.journeyFlight.flights).arrivalDisplayNames.primary

  return `${departureLocation} to ${arrivalLocation}`
}

export function formatFlightPassengersShorthand(passengers: Array<App.Checkout.FlightPassenger>): string {
  const paxMap = passengers.reduce<{ [type: string]: number }>((acc, item) => {
    return { ...acc, [item.type]: item.type in acc ? acc[item.type] + 1 : 1 }
  }, {})
  const elements = Object.keys(paxMap).map(key => {
    const count = paxMap[key]
    return `${count} ${capitalise(getPlural(key, count))}`
  })
  return elements.join(', ')
}
